import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { RemoveCircleOutline, Search, Add } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import NewPatient from "../NewPatient/NewPatient";
import { toast } from "react-toastify";
import BarcodeScanner from "../../../../Components/BarcodeScanner/BarcodeScanner";
import { playScanBeep } from "../../../../Components/BarcodeScanner/playScanBeep";
import useBarcodeLookup from "../../../../api/useBarcodeLookup";
import {
  addOrIncrementStockItem,
  sumItemTotals,
} from "../utils/scanCart";
const getCurrentDate = () => new Date().toISOString().split("T")[0];
const getCurrentTime = () =>
  new Date().toTimeString().split(":").slice(0, 2).join(":");

const AddPrescription = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();
  const location = useLocation();

  const { data: doctors } = useFetchObjects(["doctors"], "users", token);
  const { data: patients } = useFetchObjects(["patient"], "patients", token);
  const { data: medicines } = useFetchObjects(
    ["stock-medicines"],
    `stock-medicines/`,
    token,
    { staleTime: 60_000, placeholderData: (previousData) => previousData },
  );
  const { lookup } = useBarcodeLookup(token);

  const [input, setInput] = useState({
    date: getCurrentDate(),
    time: getCurrentTime(),
    doctor: "",
    discount: 0,
    patient: "",
    duration: "",
    total_price: 0,
    items: [],
  });

  const [formErrors, setFormErrors] = useState({});
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
  const autocompleteRef = useRef(null);
  const dosageRefs = useRef([]);
  const quantityRefs = useRef([]);
  const priceRefs = useRef([]);
  const notationRefs = useRef([]);
  const isBeforeRefs = useRef([]);
  const unitsPerMealRefs = useRef([]);

  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    handleAdd: handleAddPrescription,
    resetForm: success,
    loading,
  } = useAdd(
    "prescriptions",
    token,
    t("pharmacy.prescriptionCreatedSuccess"),
    t("pharmacy.prescriptionCreateFail")
  );

  useEffect(() => {
    if (success) {
      setInput({
        date: getCurrentDate(),
        time: getCurrentTime(),
        doctor: "",
        patient: "",
        discount: 0,
        duration: "",
        total_price: 0,
        items: [],
      });
      setInput((prevInput) => ({
        ...prevInput,
        patient: "",
      }));
      setFormErrors({});
    }
  }, [success]);

  const calculateTotalPrice = useCallback(
    (items) => items.reduce((sum, item) => sum + (item.total_price || 0), 0),
    []
  );

  const handleChangePrescriptionField = useCallback((e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleAddNewItem = useCallback((medicine, quantity = 1, options = {}) => {
    if (!medicine) return;
    setInput((prevInput) => {
      const newItems = addOrIncrementStockItem(
        prevInput.items,
        medicine,
        quantity
      );
      return {
        ...prevInput,
        items: newItems,
        total_price: sumItemTotals(newItems),
      };
    });
    setAutocompleteInputValue("");
    if (!options.keepScanFocus && autocompleteRef.current) {
      autocompleteRef.current.focus();
    }
  }, []);

  const handleScan = useCallback(
    async (barcode) => {
      const data = await lookup(barcode, {
        stockList: medicines || [],
      });
      if (!data.found) {
        playScanBeep(false);
        toast.error(
          data.status === "error"
            ? t("pharmacy.barcodeLookupFailed")
            : t("pharmacy.barcodeNotFound", { barcode })
        );
        return;
      }
      if (!data.best_stock || Number(data.best_stock.quantity) <= 0) {
        playScanBeep(false);
        toast.error(
          t("pharmacy.barcodeOutOfStock", {
            name: data.medicine?.name || barcode,
          })
        );
        return;
      }
      playScanBeep(true);
      handleAddNewItem(data.best_stock, 1, { keepScanFocus: true });
      toast.success(
        t("pharmacy.barcodeAddedToPrescription", {
          name: data.medicine?.name || data.best_stock.medicine_display,
        })
      );
    },
    [lookup, medicines, t, handleAddNewItem]
  );

  const appliedScansRef = useRef(false);
  useEffect(() => {
    const scanned = location.state?.scannedItems;
    if (!scanned?.length || appliedScansRef.current) return;
    appliedScansRef.current = true;
    setInput((prevInput) => {
      let items = prevInput.items;
      scanned.forEach((medicine) => {
        items = addOrIncrementStockItem(
          items,
          medicine,
          medicine.scan_quantity || 1
        );
      });
      return {
        ...prevInput,
        items,
        total_price: sumItemTotals(items),
      };
    });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const handleChangeItemField = useCallback(
    (index, e) => {
      const { name, value } = e.target;
      setInput((prevInput) => {
        const items = [...prevInput.items];
        items[index][name] =
          name === "sell_price" || name === "discount" || name === "units_per_meal"
            ? value === ""
              ? ""
              : parseFloat(value)
            : value
              ? parseInt(value, 10) || value
              : "";
        if (name === "quantity" || name === "sell_price") {
          items[index].total_price =
            items[index].sell_price * items[index].quantity;
        }
        return {
          ...prevInput,
          items,
          total_price: calculateTotalPrice(items),
        };
      });
    },
    [calculateTotalPrice]
  );

  const handleKeyDown = useCallback((e, index, field) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (field === "dosage" && unitsPerMealRefs.current[index]) {
        unitsPerMealRefs.current[index].focus();
      } else if (field === "units_per_meal" && quantityRefs.current[index]) {
        quantityRefs.current[index].focus();
      } else if (field === "quantity" && notationRefs.current[index]) {
        notationRefs.current[index].focus();
      } else if (field === "notation" && dosageRefs.current[index + 1]) {
        dosageRefs.current[index + 1].focus();
      }
    }
  }, []);

  const handleRemoveItem = useCallback(
    (index) => {
      setInput((prevInput) => {
        const items = [...prevInput.items];
        items.splice(index, 1);
        return {
          ...prevInput,
          items,
          total_price: calculateTotalPrice(items),
        };
      });
    },
    [calculateTotalPrice]
  );

  const handleSave = useCallback(
    (e) => {
      e.preventDefault();
      let isValid = true;
      let errors = {};

      if (!input.date) {
        errors.date = t("pharmacy.dateRequired");
        isValid = false;
      }

      if (!input.patient) {
        errors.patient = t("pharmacy.patientRequired");
        isValid = false;
      }
      input.items.forEach((item, index) => {
        if (!item.stock_medicine) {
          errors[`stock_medicine_${index}`] = t("pharmacy.medicineRequired");
          isValid = false;
        }
        if (!item.quantity) {
          errors[`quantity_${index}`] = t("pharmacy.quantityRequired");
          isValid = false;
        }
        if (!item.sell_price) {
          errors[`sell_price_${index}`] = t("pharmacy.priceRequired");
          isValid = false;
        }
      });

      setFormErrors(errors);
      if (isValid) {
        handleAddPrescription(input);
      }
    },
    [input, t, handleAddPrescription]
  );

  // Dialog handlers
  const handleDialogOpen = useCallback(() => setDialogOpen(true), []);
  const handleDialogClose = useCallback(() => setDialogOpen(false), []);

  // Memoize filtered medicines for better performance
  const filteredMedicines = useMemo(() => {
    const list = Array.isArray(medicines) ? medicines : medicines?.results || [];
    return list.filter((medicine) =>
      (medicine.medicine_display || "")
        .toLowerCase()
        .startsWith(autocompleteInputValue.toLowerCase())
    );
  }, [medicines, autocompleteInputValue]);

  return (
    <>
      <Card
        sx={{
          boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.1)",
          borderRadius: 4,
          backgroundColor: "#f4f6f8",
          width: "100%",
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Go Back Icon and Title */}
          <Box display="flex" alignItems="center" mb={4}>
            <Tooltip title={t("pharmacy.goBack")} arrow>
              <IconButton
                onClick={() => navigate(-1)}
                aria-label={t("pharmacy.goBack")}
                sx={{ mr: 2 }}
              >
                <ArrowBackIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography
            variant="h5"
            fontWeight={600}
            color="primary"
            textAlign="center"
            gutterBottom
          >
            {t("pharmacy.addPrescriptionTitle")}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box mb={4}>
            <Typography variant="subtitle1" fontWeight={500} mb={2}>
              {t("pharmacy.patientInfo")}
            </Typography>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={5}>
                <Autocomplete
                  options={patients?.results || []}
                  getOptionLabel={(option) =>
                    `${option.firstname} (ID: ${option.id})`
                  }
                  value={
                    patients?.results.find(
                      (patient) => patient.id === input.patient
                    ) || null
                  }
                  onChange={(event, value) =>
                    setInput((prev) => ({
                      ...prev,
                      patient: value ? value.id : "",
                    }))
                  }
                  filterOptions={(options, { inputValue }) =>
                    options.filter(
                      (option) =>
                        option.firstname
                          .toLowerCase()
                          .includes(inputValue.toLowerCase()) ||
                        String(option.id).includes(inputValue)
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("pharmacy.patient")}
                      variant="outlined"
                      error={!!formErrors.patient}
                      helperText={formErrors.patient}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={1}>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleDialogOpen}
                  sx={{ height: "100%" }}
                >
                  {t("pharmacy.add")}
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={doctors || []}
                  getOptionLabel={(option) =>
                    `${option.first_name} ${option.last_name}`
                  }
                  value={
                    doctors?.find((doc) => doc.id === input.doctor) || null
                  }
                  onChange={(event, value) =>
                    setInput((prev) => ({
                      ...prev,
                      doctor: value ? value.id : "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("pharmacy.selectDoctor")}
                      variant="outlined"
                      error={!!formErrors.doctor}
                      helperText={formErrors.doctor}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("pharmacy.prescriptionDate")}
                  name="date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={input.date}
                  onChange={handleChangePrescriptionField}
                  error={!!formErrors.date}
                  helperText={formErrors.date}
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("pharmacy.time")}
                  name="time"
                  type="time"
                  InputLabelProps={{ shrink: true }}
                  value={input.time}
                  onChange={handleChangePrescriptionField}
                  error={!!formErrors.time}
                  helperText={formErrors.time}
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("pharmacy.duration")}
                  name="duration"
                  variant="outlined"
                  value={input.duration}
                  onChange={handleChangePrescriptionField}
                  error={!!formErrors.duration}
                  helperText={formErrors.duration}
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Search Medicine Section */}
          <Box mb={4}>
            <Typography variant="subtitle1" fontWeight={500} mb={2}>
              {t("pharmacy.addMedicine")}
            </Typography>
            <Box mb={2}>
              <BarcodeScanner onScan={handleScan} listenHardware />
            </Box>
            <Autocomplete
              ref={autocompleteRef}
              value={null}
              inputValue={autocompleteInputValue}
              onInputChange={(event, newInputValue) => {
                setAutocompleteInputValue(newInputValue);
              }}
              onChange={(event, value) => {
                handleAddNewItem(value);
                setAutocompleteInputValue("");
              }}
              options={filteredMedicines}
              getOptionLabel={(option) =>
                `${option.medicine_display} (${option.category_display})`
              }
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    padding: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    backgroundColor: "#f9f9f9",
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "#eef2f5",
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ whiteSpace: "nowrap", color: "text.primary" }}
                  >
                    {option.medicine_display}
                  </Typography>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "nowrap",
                      color: "text.secondary",
                    }}
                  >
                    <strong>{t("pharmacy.category")}:</strong>{" "}
                    {option.category_display}
                  </Typography>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "nowrap",
                      color: "text.secondary",
                    }}
                  >
                    <strong>{t("pharmacy.price")}:</strong> {option.sell_price}
                  </Typography>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "nowrap",
                      color: "text.secondary",
                    }}
                  >
                    <strong>{t("pharmacy.expiryDate")}:</strong>{" "}
                    {option.expiry_date}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("pharmacy.searchMedicine")}
                  variant="outlined"
                  placeholder={t("pharmacy.searchMedicinePlaceholder")}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: 2,
                  }}
                />
              )}
            />
          </Box>

          {/* Prescription Items Table */}
          <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
            <Table>
              <TableHead sx={{ backgroundColor: "#eef2f5" }}>
                <TableRow>
                  <TableCell>{t("pharmacy.medicineName")}</TableCell>
                  <TableCell align="center">
                    {t("pharmacy.availableStock")}
                  </TableCell>
                  <TableCell align="center">{t("pharmacy.dosage")}</TableCell>
                  <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                    {t("pharmacy.unitsPerMeal")}
                  </TableCell>
                  <TableCell align="center">{t("pharmacy.quantity")}</TableCell>
                  <TableCell align="center">{t("pharmacy.price")}</TableCell>
                  <TableCell align="center">{t("pharmacy.notation")}</TableCell>
                  <TableCell align="center">
                    {t("pharmacy.isBeforeMeal")}
                  </TableCell>
                  <TableCell align="center">
                    {t("pharmacy.totalPrice")}
                  </TableCell>
                  <TableCell align="center">{t("pharmacy.action")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {input.items.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{item.name}</TableCell>
                    <TableCell align="center">{item.available_stock}</TableCell>
                    <TableCell align="center">
                      <TextField
                        inputRef={(el) => (dosageRefs.current[index] = el)}
                        name="dosage"
                        type="number"
                        size="small"
                        variant="outlined"
                        value={item.dosage}
                        onChange={(e) => handleChangeItemField(index, e)}
                        onKeyDown={(e) => handleKeyDown(e, index, "dosage")}
                        sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        inputRef={(el) =>
                          (unitsPerMealRefs.current[index] = el)
                        }
                        name="units_per_meal"
                        type="number"
                        size="small"
                        variant="outlined"
                        inputProps={{ step: "0.01", min: "0" }}
                        value={item.units_per_meal}
                        onChange={(e) => handleChangeItemField(index, e)}
                        onKeyDown={(e) =>
                          handleKeyDown(e, index, "units_per_meal")
                        }
                        sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        inputRef={(el) => (quantityRefs.current[index] = el)}
                        name="quantity"
                        type="number"
                        size="small"
                        variant="outlined"
                        value={item.quantity}
                        onChange={(e) => handleChangeItemField(index, e)}
                        onKeyDown={(e) => handleKeyDown(e, index, "quantity")}
                        error={!!formErrors[`quantity_${index}`]}
                        helperText={formErrors[`quantity_${index}`]}
                        sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        inputRef={(el) => (priceRefs.current[index] = el)}
                        name="sell_price"
                        type="number"
                        size="small"
                        variant="outlined"
                        inputProps={{ step: "0.01", min: "0" }}
                        value={item.sell_price}
                        onChange={(e) => handleChangeItemField(index, e)}
                        onKeyDown={(e) => handleKeyDown(e, index, "price")}
                        error={!!formErrors[`sell_price_${index}`]}
                        helperText={formErrors[`sell_price_${index}`]}
                        sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                        disabled
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        inputRef={(el) => (notationRefs.current[index] = el)}
                        name="notation"
                        type="text"
                        size="small"
                        variant="outlined"
                        value={item.notation}
                        onChange={(e) => handleChangeItemField(index, e)}
                        onKeyDown={(e) => handleKeyDown(e, index, "notation")}
                        sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <FormControl fullWidth size="small">
                        <Select
                          value={item.is_before}
                          onChange={(e) => handleChangeItemField(index, e)}
                          name="is_before"
                          ref={(el) => (isBeforeRefs.current[index] = el)}
                        >
                          <MenuItem value="before">
                            {t("pharmacy.before")}
                          </MenuItem>
                          <MenuItem value="med">{t("pharmacy.med")}</MenuItem>
                          <MenuItem value="after">
                            {t("pharmacy.after")}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={600}>
                        {item.total_price}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t("pharmacy.removeItem")} arrow>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <RemoveCircleOutline />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Save Button and Total Price Display */}
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <TextField
              size="small"
              label={t("pharmacy.discountAmount", "Discount Amount")}
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
              value={input.discount}
              sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
              onChange={(e) => {
                setInput({ ...input, discount: e.target.value });
              }}
            />
            <TextField
              size="small"
              label={t("pharmacy.totalPrice")}
              type="number"
              value={input.total_price}
              sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
              disabled
            />
            <Button
              size="small"
              onClick={handleSave}
              variant="contained"
              disabled={loading}
              sx={{
                fontWeight: 700,
                backgroundColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              {loading ? t("pharmacy.saving") : t("pharmacy.savePrescription")}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Dialog for Add Patient */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md">
        <DialogTitle>{t("pharmacy.addPatient", "Add New Patine")}</DialogTitle>
        <DialogContent>{dialogOpen && <NewPatient />}</DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            {t("pharmacy.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddPrescription;
